/**
 * Scenario pitch membership (SE-8343, PV12 / R08 invariant I2).
 *
 * The scenario's actions are the source of truth (SE-8628 / SE-8637 — the
 * Pitch view mirrors the Action Overview):
 * ● current squad = the roster's final state after the scenario's actions —
 * first team minus departures (SELL / LOAN_OUT leave the pitch, their slot
 * vacant) plus promoted academy players;
 * ○ arrivals = every player with a SIGN action, whichever read carries him:
 * an accessible-list target, an external row, or a roster row the scenario
 * query already moved into the first team with `transitionType: SIGN`;
 * Targets = accessible-list players not on the roster and without an arrival
 * action; First team keeps every first-team player, departures included
 * (PV14's actions and the sold flag live there); Academy = academy players
 * not promoted.
 *
 * Slots are calculated from each player's natural positions against the
 * formation's tactical slots, so a player with no compatible slot is
 * Unassigned rather than force-placed; the one exception is a slot Place ▾
 * gave an arrival (SE-8494, `placedSlots`), which wins while the formation
 * has it.
 * @module features/squad-planning/utils/deriveScenarioPitch
 */

import type {
  Member,
  PitchSquadPlayer,
  SquadPlayerDeparture,
} from "@/features/scouting/components/molecules/PitchPositionNode/PitchPositionNodeView";
import { PITCH_FORMATION_ROWS } from "@/features/scouting/constants/pitchFormationRows";
import { FORMATION_OPTIONS } from "@/features/squad-planning/constants";
import type { SquadOverlayPlayer } from "@/features/scouting/utils/watchlistPitchOverlay";
import type { PitchFormation, TacticalSlot } from "@/generated/graphql";

import type { SignedPlayerData } from "../stores/scenarioStore";
import type {
  PlayerAction,
  SquadPlanningFormation,
  SquadPlayer,
} from "../types";
import { buildDraftSignedPlayers } from "./buildDraftSignedPlayers";
import { playerMatchesRole } from "./playerMatchesRole";
import {
  projectSlotOccupantIds,
  reorderWithinGroups,
  type ScenarioSlotOrderSource,
} from "./scenarioSlotOrder";

/** Inputs the scenario pitch is derived from. */
export interface DeriveScenarioPitchArgs {
  /** Roster rows from `getSquadPlayersByPosition` (first team, academy, targets). */
  readonly players: readonly SquadPlayer[];
  /** Accessible-list targets from `listAllScoutingTargets`. */
  readonly targets: readonly SquadPlayer[];
  /** Effective action per player id (draft in planning mode, persisted otherwise). */
  readonly actions: Readonly<Record<string, PlayerAction>>;
  /** Display data for signed players that exist only as scenario rows. */
  readonly externalPlayers: Readonly<Record<string, SignedPlayerData>>;
  readonly formation: SquadPlanningFormation;
  /**
   * Arrivals a formation change orphaned (SE-8391): they stay Unassigned
   * until placed, even when a later formation offers them a slot again —
   * a revert never force-places anyone (R04).
   */
  readonly forcedUnassigned?: ReadonlySet<string>;
  /**
   * The slot Place ▾ gave an arrival (SE-8494): wins over their natural
   * slot and over the orphan memory while the formation has that slot;
   * a formation without it leaves them Unassigned (R04 — never force-placed
   * elsewhere). Absent = every slot derived.
   */
  readonly placedSlots?: Readonly<Record<string, TacticalSlot>>;
  /**
   * The persisted pitch order (SE-8393): ranks every slot's occupants — ●
   * and ○ together — with unranked ones after by GPR desc and pending
   * reorders replayed. Absent = the derived order (the watchlist pitch and
   * every caller before D9).
   */
  readonly slotOrder?: ScenarioSlotOrderSource;
}

/** A departing first-team player's action, as the pitch pill reads it. */
export type ScenarioDeparture = SquadPlayerDeparture;

/**
 * A ● squad entry the scenario pitch renders: the watchlist overlay's shape
 * (a departing player is off the pitch since SE-8637, so no entry carries
 * the departure tag).
 */
export type ScenarioSquadPlayer = PitchSquadPlayer;

/** What the scenario pitch renders. */
export interface ScenarioPitchProjection {
  /** ● current squad, placed by natural position. */
  readonly squadBySlot: Partial<
    Record<TacticalSlot, readonly ScenarioSquadPlayer[]>
  >;
  /** ○ arrivals in the shape the Pitch position node renders. */
  readonly arrivals: readonly Member[];
  /**
   * SE-8393: every slot's occupants — ● and ○ together — in slot order. The
   * single source the card's groups and the slot pop-out derive from.
   */
  readonly rankedIdsBySlot: Partial<Record<TacticalSlot, readonly string[]>>;
  /**
   * ● the squad the field draws (first team after departures, plus
   * promotions) — the baseline the three-weakest ranking reads.
   */
  readonly squad: readonly SquadPlayer[];
  /** Arrivals and promotions, for the "Added players" list. */
  readonly addedPlayers: readonly SquadPlayer[];
  readonly targets: readonly SquadPlayer[];
  readonly firstTeam: readonly SquadPlayer[];
  readonly academy: readonly SquadPlayer[];
  /** Arrivals with no compatible slot in the formation. */
  readonly unassignedCount: number;
  /** Squad players with no compatible slot (not drawn on the field). */
  readonly unplacedSquadCount: number;
}

const NO_DETAILS = {} as const;

/**
 * Maps a squad-planning formation onto the Pitch formation enum.
 * @param formation - Squad-planning formation value
 * @returns The Pitch formation with the same digits, or null when none exists
 */
export function toPitchFormation(
  formation: SquadPlanningFormation
): PitchFormation | null {
  return Object.hasOwn(PITCH_FORMATION_ROWS, formation)
    ? (formation as unknown as PitchFormation)
    : null;
}

/**
 * Maps a Pitch formation back onto the squad-planning formation (SE-8494):
 * the value the selector offers with the same digits, or null when none.
 * @param formation - Pitch formation value, as the scenario pitch read holds it
 * @returns The squad-planning formation, or null
 */
export function fromPitchFormation(
  formation: PitchFormation
): SquadPlanningFormation | null {
  // The two enums spell a formation the same way; the option list is the
  // selector's own vocabulary.
  const candidate = formation as unknown as SquadPlanningFormation;
  return FORMATION_OPTIONS.some(option => option.value === candidate)
    ? candidate
    : null;
}

/**
 * The departure tag a player's action carries, if it is one (SE-8390).
 * @param action - The player's effective action
 * @returns "Sell" / "Loan", or undefined for a non-departure
 */
export const departureOf = (
  action: PlayerAction | undefined
): ScenarioDeparture | undefined =>
  action === "Sell" || action === "Loan" ? action : undefined;

/**
 * Whether a roster row is a scenario signing (SE-8628): with a scenario id
 * the roster read already applies the scenario, so a signed player comes
 * back as a first-team row carrying `transitionType: SIGN` — an arrival,
 * not someone already in the squad.
 * @param player - A roster-query row
 * @returns True for a row the scenario signed
 */
export const isScenarioSigningRow = (player: SquadPlayer): boolean =>
  player.transitionType === "SIGN";

/**
 * Whether a roster-query row is someone already in the squad: first team or
 * academy, never a Targets-tier row nor a scenario signing (SE-8628).
 * @param player - A roster-query row
 * @returns True for a player the club holds before the scenario
 */
export const isScenarioRosterRow = (player: SquadPlayer): boolean =>
  player.tierName !== "Targets" && !isScenarioSigningRow(player);

const naturalPositionsOf = (player: SquadPlayer) =>
  player.positionAbbreviations.map(abbreviation => ({
    id: abbreviation,
    name: abbreviation,
    abbreviation,
  }));

const toSquadEntry = (player: SquadPlayer): ScenarioSquadPlayer => ({
  id: player.id,
  displayName: player.displayName,
  imageUrl: null,
  gpr: player.gprScore,
  rosterTier: (player.isAcademy
    ? "ACADEMY"
    : "FIRST_TEAM") as SquadOverlayPlayer["rosterTier"],
  naturalPositions: naturalPositionsOf(player),
});

const toArrival = (
  player: SquadPlayer,
  rank: number,
  slot: TacticalSlot | null,
  placed: boolean
): Member => ({
  id: `scenario-member-${player.id}`,
  playerId: player.id,
  rank,
  effectiveSlot: slot,
  assignmentOrigin: (slot
    ? placed
      ? "SAVED"
      : "CALCULATED"
    : "UNASSIGNED") as Member["assignmentOrigin"],
  primaryPositionCode: player.primaryPosition,
  primaryPositionSource: (player.primaryPosition
    ? "PLAYER"
    : "NONE") as Member["primaryPositionSource"],
  displayName: player.displayName,
  imageUrl: null,
  gpr: player.gprScore,
  naturalPositions: naturalPositionsOf(player),
});

/**
 * The full ranked list of every slot (SE-8393): ● squad entries and ○
 * arrivals together — persisted rank first, unranked after by GPR desc,
 * pending reorders replayed; without an order source, the derived order
 * (● then ○). The card's two groups and the slot pop-out both derive from
 * this one list, so drawing them interleaved is a rendering change only.
 * @param squadBySlot - Squad entries per slot, in derived order
 * @param arrivals - Arrivals in derived order
 * @param slotOrder - The persisted order and pending reorders, if any
 * @returns Player ids in slot order, per slot
 */
const rankSlots = (
  squadBySlot: ScenarioPitchProjection["squadBySlot"],
  arrivals: readonly Member[],
  slotOrder: ScenarioSlotOrderSource | undefined
): ScenarioPitchProjection["rankedIdsBySlot"] => {
  const slots = new Set<TacticalSlot>([
    ...(Object.keys(squadBySlot) as TacticalSlot[]),
    ...arrivals.flatMap(arrival =>
      arrival.effectiveSlot ? [arrival.effectiveSlot] : []
    ),
  ]);
  return Object.fromEntries(
    [...slots].map(slot => {
      const occupants = [
        ...(squadBySlot[slot] ?? []).map(entry => ({
          playerId: entry.id,
          gpr: entry.gpr ?? null,
        })),
        ...arrivals
          .filter(arrival => arrival.effectiveSlot === slot)
          .map(entry => ({ playerId: entry.playerId, gpr: entry.gpr ?? null })),
      ];
      return [
        slot,
        slotOrder
          ? projectSlotOccupantIds(occupants, slotOrder)
          : occupants.map(occupant => occupant.playerId),
      ];
    })
  );
};

/**
 * Sorts the card's two groups by the slot's ranked list (SE-8393): the card
 * keeps drawing ● above ○; each group follows the persisted order. Arrivals
 * are re-ranked by their new position.
 * @param squadBySlot - Squad entries per slot, in derived order
 * @param arrivals - Arrivals in derived order
 * @param rankedIdsBySlot - The full ranked list per slot
 * @returns Both, ordered
 */
const applySlotOrder = (
  squadBySlot: ScenarioPitchProjection["squadBySlot"],
  arrivals: readonly Member[],
  rankedIdsBySlot: ScenarioPitchProjection["rankedIdsBySlot"]
): Pick<ScenarioPitchProjection, "squadBySlot" | "arrivals"> => {
  const orderOf = (slot: TacticalSlot): ReadonlyMap<string, number> =>
    new Map(
      (rankedIdsBySlot[slot] ?? []).map((playerId, index) => [playerId, index])
    );
  const byOrder =
    <Item>(order: ReadonlyMap<string, number>, idOf: (item: Item) => string) =>
    (left: Item, right: Item) =>
      (order.get(idOf(left)) ?? 0) - (order.get(idOf(right)) ?? 0);
  const orderedSquad = Object.fromEntries(
    Object.entries(squadBySlot).map(([slot, entries]) => [
      slot,
      [...(entries ?? [])].sort(
        byOrder(orderOf(slot as TacticalSlot), entry => entry.id)
      ),
    ])
  ) as ScenarioPitchProjection["squadBySlot"];
  const orderedArrivals = reorderWithinGroups(
    arrivals,
    arrival => arrival.effectiveSlot ?? null,
    (slot, members) =>
      [...members].sort(byOrder(orderOf(slot), member => member.playerId))
  ).map((arrival, index) => ({ ...arrival, rank: index + 1 }));
  return { squadBySlot: orderedSquad, arrivals: orderedArrivals };
};

/**
 * Derives what the scenario pitch and its discovery panel show.
 * @param args - Roster, targets, effective actions, external rows and formation
 * @returns The projection the scenario Pitch view renders
 */
export function deriveScenarioPitch(
  args: DeriveScenarioPitchArgs
): ScenarioPitchProjection {
  const {
    players,
    targets,
    actions,
    externalPlayers,
    formation,
    forcedUnassigned,
    placedSlots,
    slotOrder,
  } = args;
  const pitchFormation = toPitchFormation(formation);
  const slots: readonly TacticalSlot[] = pitchFormation
    ? PITCH_FORMATION_ROWS[pitchFormation].flat()
    : [];
  const slotFor = (player: SquadPlayer): TacticalSlot | null =>
    slots.find(slot => playerMatchesRole(player, slot)) ?? null;
  // SE-8494: a placement the formation still has is the arrival's slot;
  // one it lacks leaves them Unassigned rather than falling back to the
  // derived slot (R04 — the placement went with the slot, nothing restores it).
  const placedSlotFor = (playerId: string): TacticalSlot | null | undefined => {
    const placed = placedSlots?.[playerId];
    if (placed === undefined) return undefined;
    return slots.includes(placed) ? placed : null;
  };

  // SE-8628: he is an arrival, not a squad member, so a signed roster row
  // is split off the roster before anything else reads it.
  const roster = players.filter(isScenarioRosterRow);
  const signedRows = players.filter(
    p => p.tierName !== "Targets" && isScenarioSigningRow(p)
  );
  const rosterIds = new Set(roster.map(p => p.id));
  const firstTeam = roster.filter(
    p => p.tierName === "First Team" || p.tierName === "Rotation"
  );
  const academyAll = roster.filter(p => p.tierName === "Academy");
  const promoted = academyAll.filter(p => actions[p.id] === "Promote");

  // SE-8637: a departing first-team player (Sell / Loan) leaves the pitch —
  // his slot is vacant for the formation and every count read from it. He
  // stays in `firstTeam`, where the panel shows him with the sold flag.
  const squad = [
    ...firstTeam.filter(p => departureOf(actions[p.id]) === undefined),
    ...promoted,
  ];
  const placedSquad = squad.map(player => ({ player, slot: slotFor(player) }));
  const squadBySlot = placedSquad.reduce<
    Partial<Record<TacticalSlot, readonly ScenarioSquadPlayer[]>>
  >(
    (bySlot, { player, slot }) =>
      slot == null
        ? bySlot
        : {
            ...bySlot,
            [slot]: [...(bySlot[slot] ?? []), toSquadEntry(player)],
          },
    {}
  );
  const unplacedSquadCount = placedSquad.filter(
    entry => entry.slot == null
  ).length;

  // I2: the accessible-list read is the only discovery set. The roster query
  // also returns Targets-tier rows, but it does not verify accessible-list
  // membership, so a player only it carries never enters the pool. A signed
  // roster row joins it (after the list's own row for the same player), so
  // the arrival survives even when no accessible list holds him, and an
  // undone draft signing falls back into Targets instead of vanishing.
  const listTargets = targets.filter(p => !rosterIds.has(p.id));
  const listTargetIds = new Set(listTargets.map(p => p.id));
  const targetPool = [
    ...listTargets,
    ...signedRows.filter(p => !listTargetIds.has(p.id)),
  ];
  const knownIds = new Set([
    ...players.map(p => p.id),
    ...targetPool.map(p => p.id),
  ]);
  const external = buildDraftSignedPlayers(
    actions,
    externalPlayers,
    NO_DETAILS,
    knownIds
  );
  const arrivalPlayers = [
    ...targetPool.filter(p => actions[p.id] === "Sign"),
    ...external,
  ];
  const arrivals = arrivalPlayers.map((player, index) => {
    const placed = placedSlotFor(player.id);
    return placed === undefined
      ? toArrival(
          player,
          index + 1,
          forcedUnassigned?.has(player.id) ? null : slotFor(player),
          false
        )
      : toArrival(player, index + 1, placed, true);
  });

  const rankedIdsBySlot = rankSlots(squadBySlot, arrivals, slotOrder);
  return {
    ...(slotOrder
      ? applySlotOrder(squadBySlot, arrivals, rankedIdsBySlot)
      : { squadBySlot, arrivals }),
    rankedIdsBySlot,
    squad,
    addedPlayers: [...arrivalPlayers, ...promoted],
    targets: targetPool.filter(p => actions[p.id] !== "Sign"),
    firstTeam,
    academy: academyAll.filter(p => actions[p.id] !== "Promote"),
    unassignedCount: arrivals.filter(a => a.effectiveSlot == null).length,
    unplacedSquadCount,
  };
}
